<template>
  <div class="cost-config">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>店铺成本配置</span>
      </div>

      <!-- 店铺选择 -->
      <el-form :inline="true" class="search-form">
        <el-form-item label="选择店铺">
          <el-select 
            v-model="selectedStoreId" 
            placeholder="请输入店铺名称搜索" 
            filterable
            clearable
            @change="loadStoreConfig"
            style="width: 300px">
            <el-option
              v-for="store in storeList"
              :key="store.id"
              :label="store.name || `店铺${store.id}`"
              :value="store.id">
              <span style="float: left">{{ store.name || `店铺${store.id}` }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">ID: {{ store.id }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="showEditDialog" :disabled="!selectedStoreId">
            配置成本
          </el-button>
          <el-button @click="showHistoryDialog" :disabled="!selectedStoreId">
            查看历史
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 当前配置展示 -->
      <div v-if="currentConfig" class="current-config">
        <h3>当前配置（生效日期：{{ currentConfig.effectiveDate }}）</h3>
        
        <!-- 固定成本 -->
        <el-card class="config-section">
          <div slot="header">
            <span>固定成本（元/月）</span>
          </div>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="cost-item">
                <span class="label">房租：</span>
                <span class="value">¥{{ formatMoney(currentConfig.costRent) }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="cost-item">
                <span class="label">水电费：</span>
                <span class="value">¥{{ formatMoney(currentConfig.costUtilities) }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="cost-item">
                <span class="label">网费：</span>
                <span class="value">¥{{ formatMoney(currentConfig.costNetwork) }}</span>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20" style="margin-top: 15px">
            <el-col :span="8">
              <div class="cost-item">
                <span class="label">人工成本：</span>
                <span class="value">¥{{ formatMoney(currentConfig.costLabor) }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="cost-item">
                <span class="label">其他成本：</span>
                <span class="value">¥{{ formatMoney(currentConfig.costOther) }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="cost-item total">
                <span class="label">固定成本合计：</span>
                <span class="value">¥{{ formatMoney(totalFixedCost) }}</span>
              </div>
            </el-col>
          </el-row>
        </el-card>

        <!-- 平台扣点 -->
        <el-card class="config-section">
          <div slot="header">
            <span>平台扣点比例</span>
          </div>
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="cost-item">
                <span class="label">美团扣点：</span>
                <span class="value">{{ currentConfig.deductionMeituan }}%</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="cost-item">
                <span class="label">抖音扣点：</span>
                <span class="value">{{ currentConfig.deductionDouyin }}%</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="cost-item">
                <span class="label">直播扣点：</span>
                <span class="value">{{ currentConfig.deductionLive }}%</span>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="cost-item">
                <span class="label">其他扣点：</span>
                <span class="value">{{ currentConfig.deductionOther }}%</span>
              </div>
            </el-col>
          </el-row>
        </el-card>

        <!-- 管理费 -->
        <el-card class="config-section">
          <div slot="header">
            <span>管理费</span>
          </div>
          <div class="cost-item">
            <span class="label">管理费率：</span>
            <span class="value">{{ currentConfig.managementFeeRate }}%</span>
          </div>
        </el-card>

        <div v-if="currentConfig.remark" class="remark">
          <strong>备注：</strong>{{ currentConfig.remark }}
        </div>
      </div>

      <div v-else-if="selectedStoreId" class="empty-state">
        <el-empty description="该店铺暂无成本配置，请点击"配置成本"按钮添加"></el-empty>
      </div>
    </el-card>

    <!-- 编辑对话框 -->
    <el-dialog title="配置店铺成本" :visible.sync="editDialogVisible" width="800px">
      <el-form :model="editForm" label-width="120px">
        <el-divider content-position="left">固定成本（元/月）</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="房租">
              <el-input-number v-model="editForm.costRent" :min="0" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="水电费">
              <el-input-number v-model="editForm.costUtilities" :min="0" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="网费">
              <el-input-number v-model="editForm.costNetwork" :min="0" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="人工成本">
              <el-input-number v-model="editForm.costLabor" :min="0" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="其他成本">
              <el-input-number v-model="editForm.costOther" :min="0" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="固定成本合计">
              <el-input-number v-model="editForm.totalFixedCost" :min="0" :precision="2" style="width: 100%" placeholder="可手动输入或自动计算">
                <template slot="append">
                  <el-button @click="autoCalculateTotal" size="small">自动计算</el-button>
                </template>
              </el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">平台扣点比例（%）</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="美团扣点">
              <el-input-number v-model="editForm.deductionMeituan" :min="0" :max="100" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="抖音扣点">
              <el-input-number v-model="editForm.deductionDouyin" :min="0" :max="100" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="直播扣点">
              <el-input-number v-model="editForm.deductionLive" :min="0" :max="100" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="其他扣点">
              <el-input-number v-model="editForm.deductionOther" :min="0" :max="100" :precision="2" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">管理费</el-divider>
        <el-form-item label="管理费率（%）">
          <el-input-number v-model="editForm.managementFeeRate" :min="0" :max="100" :precision="2" style="width: 200px"></el-input-number>
        </el-form-item>

        <el-divider content-position="left">其他信息</el-divider>
        <el-form-item label="生效日期">
          <el-date-picker
            v-model="editForm.effectiveDate"
            type="date"
            placeholder="选择生效日期"
            value-format="yyyy-MM-dd">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="editForm.remark" type="textarea" :rows="3"></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveConfig">保存配置</el-button>
      </div>
    </el-dialog>

    <!-- 历史记录对话框 -->
    <el-dialog title="配置历史" :visible.sync="historyDialogVisible" width="900px">
      <el-timeline>
        <el-timeline-item
          v-for="(config, index) in historyConfigs"
          :key="index"
          :timestamp="config.effectiveDate"
          placement="top">
          <el-card>
            <h4>{{ config.effectiveDate }} ~ {{ config.endDate || '至今' }}</h4>
            <el-descriptions :column="3" border size="small">
              <el-descriptions-item label="房租">¥{{ formatMoney(config.costRent) }}</el-descriptions-item>
              <el-descriptions-item label="水电">¥{{ formatMoney(config.costUtilities) }}</el-descriptions-item>
              <el-descriptions-item label="网费">¥{{ formatMoney(config.costNetwork) }}</el-descriptions-item>
              <el-descriptions-item label="人工">¥{{ formatMoney(config.costLabor) }}</el-descriptions-item>
              <el-descriptions-item label="其他">¥{{ formatMoney(config.costOther) }}</el-descriptions-item>
              <el-descriptions-item label="固定成本合计">
                <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(calculateTotal(config)) }}</span>
              </el-descriptions-item>
              <el-descriptions-item label="美团扣点">{{ config.deductionMeituan }}%</el-descriptions-item>
              <el-descriptions-item label="抖音扣点">{{ config.deductionDouyin }}%</el-descriptions-item>
              <el-descriptions-item label="直播扣点">{{ config.deductionLive }}%</el-descriptions-item>
              <el-descriptions-item label="管理费率" :span="3">{{ config.managementFeeRate }}%</el-descriptions-item>
            </el-descriptions>
            <div v-if="config.remark" class="history-remark">备注：{{ config.remark }}</div>
            <div class="history-meta">
              <span>操作人：{{ config.createBy || '系统' }}</span>
              <span>创建时间：{{ config.createTime }}</span>
            </div>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'StoreCostConfig',
  data() {
    return {
      storeList: [],
      selectedStoreId: null,
      currentConfig: null,
      editDialogVisible: false,
      historyDialogVisible: false,
      editForm: {
        storeId: null,
        costRent: 0,
        costUtilities: 0,
        costNetwork: 0,
        costLabor: 0,
        costOther: 0,
        totalFixedCost: 0,
        deductionMeituan: 0,
        deductionDouyin: 0,
        deductionLive: 0,
        deductionOther: 0,
        managementFeeRate: 0,
        effectiveDate: '',
        remark: ''
      },
      historyConfigs: []
    }
  },
  computed: {
    totalFixedCost() {
      if (!this.currentConfig) return 0
      return (
        parseFloat(this.currentConfig.costRent || 0) +
        parseFloat(this.currentConfig.costUtilities || 0) +
        parseFloat(this.currentConfig.costNetwork || 0) +
        parseFloat(this.currentConfig.costLabor || 0) +
        parseFloat(this.currentConfig.costOther || 0)
      )
    },
    editTotalFixedCost() {
      return (
        parseFloat(this.editForm.costRent || 0) +
        parseFloat(this.editForm.costUtilities || 0) +
        parseFloat(this.editForm.costNetwork || 0) +
        parseFloat(this.editForm.costLabor || 0) +
        parseFloat(this.editForm.costOther || 0)
      )
    }
  },
  mounted() {
    this.loadStoreList()
  },
  methods: {
    // 加载店铺列表
    async loadStoreList() {
      try {
        const response = await this.$http.get('/api/v2/store-owner/stores')
        if (response.data.status === 0) {
          // 兼容不同的字段名：name, storename, storeName
          this.storeList = (response.data.data || []).map(store => ({
            id: store.id || store.storeId,
            name: store.name || store.storename || store.storeName || `店铺${store.id || store.storeId}`
          }))
          console.log('店铺列表:', this.storeList)
        }
      } catch (error) {
        console.error('加载店铺列表失败:', error)
        this.$message.error('加载店铺列表失败')
      }
    },

    // 加载店铺配置
    async loadStoreConfig() {
      if (!this.selectedStoreId) return

      try {
        const response = await this.$http.get(`/api/profit/cost-config/store/${this.selectedStoreId}`)
        
        if (response.data.status === 0) {
          this.currentConfig = response.data.data
        } else {
          this.currentConfig = null
        }
      } catch (error) {
        this.$message.error('加载配置失败')
      }
    },

    // 显示编辑对话框
    showEditDialog() {
      if (this.currentConfig) {
        this.editForm = {
          ...this.currentConfig,
          effectiveDate: this.formatDate(new Date())
        }
      } else {
        this.editForm = {
          storeId: this.selectedStoreId,
          costRent: 0,
          costUtilities: 0,
          costNetwork: 0,
          costLabor: 0,
          costOther: 0,
          totalFixedCost: 0,
          deductionMeituan: 0,
          deductionDouyin: 0,
          deductionLive: 0,
          deductionOther: 0,
          managementFeeRate: 0,
          effectiveDate: this.formatDate(new Date()),
          remark: ''
        }
      }
      this.editDialogVisible = true
    },

    // 自动计算固定成本合计
    autoCalculateTotal() {
      this.editForm.totalFixedCost = (
        parseFloat(this.editForm.costRent || 0) +
        parseFloat(this.editForm.costUtilities || 0) +
        parseFloat(this.editForm.costNetwork || 0) +
        parseFloat(this.editForm.costLabor || 0) +
        parseFloat(this.editForm.costOther || 0)
      )
      this.$message.success('已自动计算固定成本合计')
    },

    // 保存配置
    async saveConfig() {
      try {
        this.editForm.storeId = this.selectedStoreId
        // 安全获取用户名
        this.editForm.createBy = (this.$store && this.$store.state && this.$store.state.adminInfo) 
          ? this.$store.state.adminInfo.username 
          : 'admin'
        
        const response = await this.$http.post('/api/profit/cost-config/save', this.editForm)

        if (response.data.status === 0) {
          this.$message.success('配置保存成功')
          this.editDialogVisible = false
          this.loadStoreConfig()
        } else {
          this.$message.error(response.data.message || '保存失败')
        }
      } catch (error) {
        this.$message.error('保存失败：' + error.message)
      }
    },

    // 查看历史
    async showHistoryDialog() {
      if (!this.selectedStoreId) {
        this.$message.warning('请先选择店铺')
        return
      }

      try {
        const response = await this.$http.get(`/api/profit/cost-config/store/${this.selectedStoreId}/history`)
        
        if (response.data.status === 0) {
          // 按生效日期倒序排序
          this.historyConfigs = response.data.data.sort((a, b) => {
            return new Date(b.effectiveDate) - new Date(a.effectiveDate)
          })
          this.historyDialogVisible = true
        }
      } catch (error) {
        this.$message.error('加载历史记录失败')
      }
    },

    // 计算总成本（如果配置了totalFixedCost就用配置的，否则自动计算）
    calculateTotal(config) {
      if (config.totalFixedCost !== undefined && config.totalFixedCost !== null) {
        return parseFloat(config.totalFixedCost || 0)
      }
      return (
        parseFloat(config.costRent || 0) +
        parseFloat(config.costUtilities || 0) +
        parseFloat(config.costNetwork || 0) +
        parseFloat(config.costLabor || 0) +
        parseFloat(config.costOther || 0)
      )
    },

    // 格式化金额
    formatMoney(value) {
      if (!value) return '0.00'
      return parseFloat(value).toFixed(2)
    },

    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.cost-config {
  padding: 20px;
}

.search-form {
  margin-bottom: 20px;
}

.current-config {
  margin-top: 20px;
}

.current-config h3 {
  margin-bottom: 20px;
  color: #303133;
}

.config-section {
  margin-bottom: 20px;
}

.cost-item {
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.cost-item .label {
  color: #606266;
  font-size: 14px;
}

.cost-item .value {
  color: #303133;
  font-size: 16px;
  font-weight: bold;
  margin-left: 10px;
}

.cost-item.total {
  background: #ecf5ff;
}

.cost-item.total .value {
  color: #409eff;
  font-size: 18px;
}

.remark {
  margin-top: 20px;
  padding: 15px;
  background: #fff9e6;
  border-left: 4px solid #e6a23c;
  border-radius: 4px;
}

.empty-state {
  padding: 40px 0;
}

.history-remark {
  margin-top: 10px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
}

.history-meta {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.history-meta span {
  margin-right: 20px;
}
</style>
